import { Button, VisuallyHidden } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { parse } from 'qs'
import { useState } from 'react'
import { useSearch } from 'wouter'

import logoFullLightPunchout from '@/assets/images/logo-full-light-punchout.png'
import { PricingTiersDisplay } from '@/components/PricingTiersDisplay'
import { useGlobalState } from '@/hooks/useGlobalState'
import * as styles from '@/styles/OnboardingScreen.css'

import { AccountConfirmationStep } from './AccountConfirmationStep'
import { EmailVerificationStep } from './EmailVerificationStep'
import { SignUpStep } from './SignUpStep'

export function OnboardingScreen() {
  const { api } = useGlobalState()
  const searchParamsString = useSearch()
  const searchParams: { step?: string } = parse(searchParamsString)
  // Skip the pricing-info step when arriving from the "Create a New Channel" flow
  const initialStep = searchParams.step === 'signup' ? 1 : 0
  const [activeStep, setActiveStep] = useState(initialStep)
  const [customerInfoUuid, setCustomerInfoUuid] = useState<string | null>(null)
  const [email, setEmail] = useState<string>('')
  const [isAccountSetupComplete, setIsAccountSetupComplete] = useState(false)

  const plansQuery = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.getPlans(),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const handleTierInfoContinue = () => {
    setActiveStep(1)
  }

  const handleSignUpComplete = (customerInfoId: string, userEmail: string) => {
    setCustomerInfoUuid(customerInfoId)
    setEmail(userEmail)
    setActiveStep(2)
  }

  const handleEmailVerificationComplete = () => {
    setActiveStep(3)
  }

  const handleAccountSetupComplete = () => {
    setIsAccountSetupComplete(true)
  }

  const renderCurrentStep = () => {
    // Step 0: Tier Information
    if (activeStep === 0) {
      return (
        <PricingTiersDisplay
          plans={plansQuery.data || []}
          actions={
            <Button onClick={handleTierInfoContinue} size="md" color="indigo">
              Continue
            </Button>
          }
        />
      )
    }

    // Step 1: Sign Up
    if (activeStep === 1) {
      return <SignUpStep onComplete={handleSignUpComplete} />
    }

    // Step 2: Email Verification
    if (activeStep === 2) {
      if (customerInfoUuid && email) {
        return (
          <EmailVerificationStep
            email={email}
            customerInfoId={customerInfoUuid}
            onComplete={handleEmailVerificationComplete}
          />
        )
      }
      return <div>Loading...</div>
    }

    // Step 3: Account Confirmation (or completed state)
    if (activeStep === 3 || isAccountSetupComplete) {
      if (customerInfoUuid) {
        return <AccountConfirmationStep customerInfoUuid={customerInfoUuid} onComplete={handleAccountSetupComplete} />
      }
      return <div>Loading...</div>
    }

    // Fallback
    return <div>Loading...</div>
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <VisuallyHidden component="h1">Sign up for Sidekick Platform</VisuallyHidden>
        <header className={styles.header}>
          <img src={logoFullLightPunchout} alt="Sidekick Platform" className={styles.logo} />
        </header>

        <div className={styles.stepperCard}>
          <div className={styles.stepContent}>{renderCurrentStep()}</div>
        </div>
      </div>
    </div>
  )
}
